import { JSONNode } from "./schema";

/**
 * Renders template content (TipTap JSON) to a static HTML string for use as a
 * lightweight, non-interactive preview/thumbnail.
 *
 * Ported from the web portal's Angular TemplatePreviewService (kept as reference
 * at docs/template-preview.service.ts) — Angular/sanitizer bits stripped, node
 * vocabulary unchanged. The output is first-party (our own presets, already
 * normalized), so it is safe to inject via dangerouslySetInnerHTML.
 */

const FONT_FAMILY = `Roboto, Arial, Helvetica, sans-serif`;
const RELATIVE_FACTOR = 0.5;

const hasMark = (item: JSONNode, type: string) => item.marks?.some((m) => m.type === type) ?? false;

function getTextSpans(content: JSONNode[] = []): string {
  let spans = "";
  for (const item of content) {
    const isBold = hasMark(item, "bold");
    const isUnderline = hasMark(item, "underline");
    const isItalic = hasMark(item, "italic");
    const color = item.marks?.find((m) => m.type === "textStyle")?.attrs?.color ?? "black";

    const style = `font-weight: ${isBold ? "bold" : "inherit"}; color: ${color}; font-style: ${
      isItalic ? "italic" : "normal"
    }; text-decoration: ${isUnderline ? "underline" : "none"};`;

    if (item.type === "text") {
      spans += `<span style="${style}">${item.text ?? "&nbsp;"}</span>`;
    } else if (item.type === "mention") {
      spans += `<span style="${style}">${item.attrs?.label ?? item.attrs?.id ?? ""}</span>`;
    }
  }
  return spans;
}

function getHeading(data: JSONNode): string {
  const level = data.attrs?.level ?? 1;
  const textAlign = data.attrs?.textAlign ?? "left";
  const textSpans = getTextSpans(data.content);

  let headingSize = 1;
  switch (level) {
    case 1:
      headingSize = 0.7;
      break;
    case 2:
      headingSize = 0.6;
      break;
    case 3:
      headingSize = 0.4;
      break;
  }

  return `<h${level} style="font-weight: normal; font-family: ${FONT_FAMILY}; text-align: ${textAlign}; font-size: ${headingSize}rem; margin: 0.2rem 0px;">${textSpans}</h${level}>`;
}

function getParagraph(data: JSONNode): string {
  const textAlign = data.attrs?.textAlign ?? "left";
  const textSpans = data.content && data.content.length > 0 ? getTextSpans(data.content) : "<span>&nbsp;</span>";
  return `<p style="text-align: ${textAlign}; margin: 0px; color: black; font-size: 0.6em;">${textSpans}</p>`;
}

function getHorizontalLine(data: JSONNode): string {
  const color = data.attrs?.color ?? "black";
  const height = (data.attrs?.height ?? 0.5) * RELATIVE_FACTOR;
  const width = data.attrs?.width ?? 1;
  const alignment = data.attrs?.alignment ?? "left";

  const flexAlign =
    alignment === "center" ? "justify-center" : alignment === "right" ? "justify-content-end" : "justify-content-start";

  return `<div class="flex flex-row ${flexAlign}"><div style="height: ${height}px; width: ${width}%; background-color: ${color}; margin: 0.1rem 0px;"></div></div>`;
}

function getSpacerBlock(data: JSONNode): string {
  const height = (data.attrs?.height ?? 1) * RELATIVE_FACTOR;
  return `<div style="height: ${height}px; width: 100%;"></div>`;
}

function getImageBlock(data: JSONNode, storageToken: string): string {
  const width = data.attrs?.width ?? 1;
  const alignment = data.attrs?.align ?? "left";
  const type = data.attrs?.type ?? "";
  let imageSrc = data.attrs?.src ?? "";

  if (imageSrc === "") return `<div></div>`;

  const flexAlign =
    alignment === "center" ? "justify-center" : alignment === "right" ? "justify-content-end" : "justify-content-start";

  // Logo / project placeholders are already fully-qualified; other images need the storage token.
  if (type !== "project-image" && type !== "logo" && storageToken && !imageSrc.includes("?")) {
    imageSrc += `?${storageToken}`;
  }

  return `<div class="flex flex-row ${flexAlign}"><img style="width: ${width}; border-radius: 5px; background: var(--surface-200)" src="${imageSrc}" /></div>`;
}

function getSignatureBlock(data: JSONNode): string {
  const textSpans = getTextSpans(data.content);
  return `<div class="w-full flex flex-row items-end h-[1rem]"><div>${textSpans}</div><div class="flex-1 border-b-1 border-dotted border-neutral-300"></div></div>`;
}

function getListItems(content: JSONNode[] = []): string {
  let items = "";
  for (const item of content) {
    if (item.type !== "listItem") continue;
    for (const child of item.content ?? []) {
      if (child.type === "paragraph") items += `<li>${getParagraph(child)}</li>`;
    }
  }
  return items;
}

// Both lists must share the same left indent — the editor's own lists.css (`px-8` on both ul
// and ol) treats bullet and ordered lists identically, and this preview previously didn't
// (ml-3 vs ml-2), so ordered lists rendered with one less indent unit than in the editor,
// giving wrapped lines slightly more room here than they'd actually have once produced.
function getBulletList(data: JSONNode): string {
  return `<ul class="list-disc list-outside ml-3">${getListItems(data.content)}</ul>`;
}

function getOrderedList(data: JSONNode): string {
  return `<ol class="list-decimal list-outside ml-3">${getListItems(data.content)}</ol>`;
}

function getColumns(data: JSONNode, storageToken: string): string {
  let leftColumn = "";
  let rightColumn = "";

  for (const col of data.content ?? []) {
    const columnHtml = Array.isArray(col.content) && col.content.length > 0 ? buildHtml(col.content, storageToken) : "";
    if (col.attrs?.position === "left") leftColumn += columnHtml;
    if (col.attrs?.position === "right") rightColumn += columnHtml;
  }

  let leftWidth = 50;
  let rightWidth = 50;
  if (data.attrs?.layout === "sidebar-left") {
    leftWidth = 33;
    rightWidth = 67;
  } else if (data.attrs?.layout === "sidebar-right") {
    leftWidth = 67;
    rightWidth = 33;
  }

  return `<div class="flex flex-row"><div style="width: ${leftWidth}%">${leftColumn}</div><div style="width: ${rightWidth}%">${rightColumn}</div></div>`;
}

function buildHtml(data: JSONNode[], storageToken: string): string {
  let html = "";
  if (!Array.isArray(data)) return html;

  for (const el of data) {
    switch (el?.type) {
      case "columns":
        html += getColumns(el, storageToken);
        break;
      case "heading":
        html += getHeading(el);
        break;
      case "paragraph":
        html += getParagraph(el);
        break;
      case "horizontalLine":
        html += getHorizontalLine(el);
        break;
      case "spacerBlock":
        html += getSpacerBlock(el);
        break;
      case "imageBlock":
        html += getImageBlock(el, storageToken);
        break;
      case "bulletList":
        html += getBulletList(el);
        break;
      case "orderedList":
        html += getOrderedList(el);
        break;
      case "signature":
        html += getSignatureBlock(el);
        break;
      default:
        break;
    }
  }

  return html;
}

/** Render template content to a static HTML string for preview/thumbnails. */
export function coverPreviewHtml(content: unknown, storageToken = ""): string {
  const data = Array.isArray(content) ? (content as JSONNode[]) : [];
  return `<div style="font-family: ${FONT_FAMILY}; padding: 1.3rem; font-size: 0.5rem;">${buildHtml(data, storageToken)}</div>`;
}
